<template>
    <modal-dialog
        ref="emailCredentialsModal"
        description="Hier kun je de email koppeling voor je vestiging instellen"
        label="E-mail koppeling formulier"
    >
        <flex-container>
            <flex-container gap="sm" direction="column">
                <text-decorator bold :size="FontSize.xs">
                    Vestigingen
                </text-decorator>
                <text-heading>
                    E-mail koppeling beheren
                </text-heading>
                <text-description>
                    Vul de email gegevens bijhorend bij de vestiging om deze te koppelen.
                </text-description>
            </flex-container>

            <flex-container>
                <flex-container
                    :screen-sizes="{
                        laptop: 2,
                    }"
                >
                    <data-group>
                        <data-label>
                            Email account
                        </data-label>
                        <data-value>
                            Vul hier je login gegevens van het email account van de vestiging in
                        </data-value>
                    </data-group>
                </flex-container>

                <flex-container
                    :screen-sizes="{
                        laptop: 8,
                    }"
                >
                    <input-group v-model="form.username">
                        <template #label>
                            <input-label>
                                Gebruikersnaam
                            </input-label>
                        </template>
                        <template #input>
                            <text-input />
                        </template>
                    </input-group>
                    <input-group v-model="form.password">
                        <template #label>
                            <input-label>
                                Wachtwoord
                            </input-label>
                        </template>
                        <template #input>
                            <text-input password />
                        </template>
                    </input-group>
                    <button-block @loading="validate($event)">
                        Valideren
                    </button-block>
                </flex-container>
            </flex-container>

            <flex-container
                v-if="emailCredentialsModal"
                justify-content="flex-end"
                align-items="center"
            >
                <button-text @click="cancel">
                    Terug
                </button-text>
                <button-block @loading="confirm(); $event.button.setState(ButtonState.completed)">
                    Bevestigen
                </button-block>
            </flex-container>
        </flex-container>
    </modal-dialog>
</template>

<script setup lang="ts">
    import { type ExposedOverlay, FontSize, ButtonState, NotificationBlock, useNotify } from '@keyvue/one-ui';
    import { ref } from 'vue';
    import useClient from '@/services/keyvue-api/composables/useClient';

    export interface Form {
        imapHostName: string
        imapPort: string
        smtpHostName: string
        smtpPort: string
        username: string
        password: string
    }

    export interface ExposedEmailCredentialsForm {
        form: Form,
        setForm: (values: Form) => void,
        overlay: ExposedOverlay,
    }

    const emailCredentialsModal = ref<ExposedOverlay>();

    const { client } = useClient();
    const { notify } = useNotify();

    const form = ref<Form>({
        imapHostName: import.meta.env.VITE_REALWORKS_IMAP_HOST,
        imapPort: import.meta.env.VITE_REALWORKS_IMAP_PORT,
        smtpHostName: import.meta.env.VITE_REALWORKS_SMTP_HOST,
        smtpPort: import.meta.env.VITE_REALWORKS_SMTP_PORT,
        username: '',
        password: '',
    });

    const emit = defineEmits(['confirmForm', 'cancelForm']);

    async function validate(event) {
        const response: any = await client.client.post('/api/email/credentials/validate', JSON.stringify({
            imapHostName: form.value.imapHostName,
            imapPort: form.value.imapPort,
            smtpHostName: form.value.smtpHostName,
            smtpPort: form.value.smtpPort,
            username: form.value.username,
            password: form.value.password,
        }));

        const imapValid = response.data.imapValid as boolean;
        const smtpValid = response.data.smtpValid as boolean;
        const bothValid = imapValid && smtpValid;

        if (bothValid) {
            notify({
                component: NotificationBlock,
                props: {
                    title: 'Valideren gelukt!',
                    text: 'Met deze gegevens hebben we succesvol verbinding gemaakt met IMAP en SMTP',
                    color: 'green-500',
                },
                duration: 4000,
            });

            event.button.setState(ButtonState.completed);
            return;
        }

        if (!imapValid) {
            notify({
                component: NotificationBlock,
                props: {
                    title: 'Valideren mislukt!',
                    text: 'Met deze gegevens konden we geen verbinding maken met IMAP',
                    color: 'red-500',
                },
                duration: 4000,
            });
        }

        if (!smtpValid) {
            notify({
                component: NotificationBlock,
                props: {
                    title: 'Valideren mislukt!',
                    text: 'Met deze gegevens konden we geen verbinding maken met SMTP',
                    color: 'red-500',
                },
                duration: 4000,
            });
        }

        event.button.setState(ButtonState.completed);
    }

    function confirm() {
        if (!emailCredentialsModal.value) {
            return;
        }

        emailCredentialsModal.value.closeOverlay();

        emit('confirmForm');
    }

    function cancel() {
        if (!emailCredentialsModal.value) {
            return;
        }

        emailCredentialsModal.value.closeOverlay();

        emit('cancelForm');
    }

    function setForm(values: Form) {
        form.value.username = values.username;
        form.value.password = values.password;
    }

    defineExpose({
        overlay: emailCredentialsModal,
        setForm,
        form,
    });
</script>
